import {
  AspectRatio,
  Badge,
  Box,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { PageContainer, PageHeader } from 'components/ds';
import { Play } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResourceItem, RESOURCES } from './resources-data';

/** Extract the 11-char video id from a YouTube watch/short/embed URL (null if not YouTube). */
const youtubeId = (url?: string): string | null => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
};

/** Embeddable /embed URL for the modal player (null if not YouTube). */
const toYoutubeEmbed = (url?: string): string | null => {
  const id = youtubeId(url);
  // mute=1 guarantees autoplay (browsers block sound-on autoplay) so the player never shows
  // YouTube's pre-play cover (dark title bar + big play button). controls=0 hides the control bar
  // & its gradient. enablejsapi lets our overlay toggle play/pause. modestbranding drops the logo.
  // (#78785)
  return id
    ? `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&rel=0&controls=0&modestbranding=1&playsinline=1&enablejsapi=1`
    : null;
};

/** Video thumbnail image URL (null if not YouTube). */
const toYoutubeThumb = (url?: string): string | null => {
  const id = youtubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

/**
 * Resources (AO-04) — built-in list of short how-to videos.
 * Frontend-only static content per SoW. Matches mockup ResourcesScreen.jsx +
 * .resource-* styles. DS tokens + Card pattern + lucide icons. Light + dark.
 */

const Resources = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [active, setActive] = useState<ResourceItem | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playingRef = useRef(true); // autoplay=1&mute=1 → always starts playing

  // A transparent layer over the iframe stops the mouse reaching YouTube's player, suppressing its
  // hover chrome (the dark top title bar). Clicking it toggles play/pause via the IFrame API. (#78785)
  const togglePlay = () => {
    const func = playingRef.current ? 'pauseVideo' : 'playVideo';
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args: [] }),
      '*'
    );
    playingRef.current = !playingRef.current;
  };

  // Play the resource video INLINE in a modal (embedded player) instead of opening a new
  // tab. Non-YouTube links still open in a new tab; missing links show a notice. (#78785)
  const onPlay = (v: ResourceItem) => {
    if (v.url && toYoutubeEmbed(v.url)) {
      playingRef.current = true; // fresh autoplay each time the modal opens
      setActive(v);
      onOpen();
      return;
    }
    if (v.url) {
      window.open(v.url, '_blank', 'noopener,noreferrer');
      return;
    }
    toast({
      title: t('resources.comingSoon', { defaultValue: 'This video is coming soon.' }),
      status: 'info',
      position: 'bottom-right',
      isClosable: true,
    });
  };

  const handleClose = () => {
    onClose();
    setActive(null); // unmount iframe so the video stops playing
  };

  return (
    <PageContainer>
      {/* Page header — shared DS PageHeader (left-aligned title + muted subtitle) */}
      <PageHeader
        title={t('route.resources', { defaultValue: 'Resources' })}
        subtitle={t('resources.subtitle', {
          defaultValue: 'Short how-to videos to help you get the most out of Amatrium.',
        })}
      />

      {/* Card grid — 4-up desktop / 3-up tablet / 1-up mobile (QA round-2: match mockup density) */}
      <Grid
        gap={4}
        templateColumns={{
          base: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(3, minmax(0, 1fr))',
          xl: 'repeat(4, minmax(0, 1fr))',
        }}
      >
        {RESOURCES.map((v) => (
          <Box
            key={v.id}
            overflow='hidden'
            bg='bg.surface'
            borderWidth='1px'
            borderColor='border.default'
            borderRadius='lg'
            shadow='none'
            _hover={{ boxShadow: 'ds_sm', borderColor: 'border.strong' }}
            transition='box-shadow .16s ease, border-color .16s ease'
            role='group'
          >
            {/* Thumbnail — real YouTube thumbnail when available, gradient fallback +
                centered play button (mockup .resource-thumb) (#78785) */}
            <Flex
              as='button'
              pos='relative'
              align='center'
              justify='center'
              w='100%'
              h={{ base: '100px', xl: '110px' }}
              bgImage={toYoutubeThumb(v.url) ? `url(${toYoutubeThumb(v.url)})` : undefined}
              bgSize='cover'
              bgPosition='center'
              bgGradient={
                toYoutubeThumb(v.url) ? undefined : 'linear(135deg, primary.soft, bg.surfaceRaised)'
              }
              border='none'
              borderColor='border.default'
              borderBottomWidth='1px'
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'primary.solid',
                outlineOffset: '-2px',
              }}
              cursor='pointer'
              aria-label={`${t('resources.play', { defaultValue: 'Play' })}: ${v.title}`}
              onClick={() => onPlay(v)}
              type='button'
            >
              {v.url ? (
                <Flex
                  align='center'
                  justify='center'
                  w='40px'
                  h='40px'
                  color='primary.solid'
                  bg='bg.surface'
                  borderRadius='full'
                  shadow='ds_sm'
                  _groupHover={{ transform: 'scale(1.08)' }}
                  transition='transform .15s ease'
                >
                  {/* Filled indigo triangle per mockup — stroke='none' so it
                      renders solid, not an outline (QA: outline triangle). */}
                  <Play size={18} fill='currentColor' stroke='none' />
                </Flex>
              ) : (
                // No video yet → show a "Coming soon" pill on the thumbnail instead of a play button.
                <Text
                  px={3}
                  py={1}
                  color='text.muted'
                  fontSize='xs'
                  fontWeight={600}
                  letterSpacing='0.04em'
                  textTransform='uppercase'
                  bg='bg.surface'
                  borderRadius='full'
                  shadow='ds_sm'
                >
                  {t('resources.comingSoonBadge', { defaultValue: 'Coming soon' })}
                </Text>
              )}
              {/* Duration badge removed — no reliable way to show the linked video's real
                  length without the YouTube Data API, and hardcoded times were misleading. (#78785) */}
            </Flex>

            {/* Body — title, description, topic badge (mockup .resource-body) */}
            <Box p={4}>
              <Text mb={1} color='text.default' fontSize='sm' fontWeight={500}>
                {v.title}
              </Text>
              <Text mb={3} color='text.muted' fontSize='xs' lineHeight={1.5}>
                {v.desc}
              </Text>
              <Badge variant='ds-neutral'>{v.topic}</Badge>
            </Box>
          </Box>
        ))}
      </Grid>

      {/* Inline video player — embedded YouTube in a modal (no new tab). (#78785) */}
      <Modal isCentered isOpen={isOpen} onClose={handleClose} size='4xl'>
        <ModalOverlay bg='blackAlpha.700' backdropFilter='blur(2px)' />
        <ModalContent overflow='hidden' bg='bg.surface' borderRadius='lg'>
          <ModalCloseButton zIndex={3} color='text.default' />
          <ModalBody p={0}>
            <Box pos='relative'>
              <AspectRatio ratio={16 / 9}>
                {active && toYoutubeEmbed(active.url) ? (
                  <iframe
                    ref={iframeRef}
                    title={active.title}
                    src={toYoutubeEmbed(active.url) as string}
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                    style={{ border: 0 }}
                  />
                ) : (
                  <Box />
                )}
              </AspectRatio>
              {/* Transparent click-catch: blocks YouTube hover chrome (dark top title bar);
                  click toggles play/pause via the IFrame API. Sits below the close button (z=3). */}
              {active && (
                <Box
                  pos='absolute'
                  zIndex={1}
                  cursor='pointer'
                  aria-hidden
                  inset={0}
                  onClick={togglePlay}
                />
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default Resources;
